import { Box, useMediaQuery } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Card from '../../shared/layout/Card'
import LegendItem from './LegendItem'
import palette from '../../theme/palette'
import { RootStore, useAppDispatch } from '../../redux/store'
import { isMobilePortrait } from '../../theme/deviceChecks'
import { setActiveRoute } from '../../redux/reducers/appSettingsReducer'
import { Entitlement } from '../../models/entitlement'
import CircularProgressBar from '../../shared/UI/CircularProgressBar'
import InfoPopper from '../../shared/UI/InfoPopper'
import Drawer from '../../shared/UI/Drawer'
import BuyRequest from '../BuyRequest'
import { BuyHoursType } from '../../types/buy-hours-type'
import { SellHoursType } from '../../types/sell-hours-type'
import SellRequest from '../SellRequest'
import FullComponentLoadingIcon from '../../shared/UI/LoadingIndicator/FullComponentLoadingIcon'
import Link from '../../shared/UI/Link'
import Paragraph from '../../shared/UI/Paragraph'
import { MyEntitlementResponse } from '../../types/my-entitlement-response'
import actionsService from '../../services/actionsService'

type Props = {
  title: string
  buyHours?: BuyHoursType | undefined
  sellHours?: SellHoursType | undefined
  myEntitlement?: MyEntitlementResponse | undefined
  isLoadingEntitlement: boolean
  requestedSellHours?: number
  refreshEntitlement?: () => void
  fetchSellData?: () => void
}

function AllowanceChart({
  title,
  sellHours,
  buyHours,
  myEntitlement,
  isLoadingEntitlement,
  requestedSellHours,
  refreshEntitlement,
  fetchSellData,
}: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { userSettings } = useSelector((state: RootStore) => state.appSettings)

  const [holidayEntitlement, setHolidayEntitlement] = useState<Entitlement | null>(null)
  const [openDrawer, setOpenDrawer] = useState(false)
  const [drawerType, setDrawerType] = useState('')

  const isMobPortrait = useMediaQuery(isMobilePortrait())
  const isLoading =
    !userSettings ||
    !holidayEntitlement ||
    (userSettings.isBuyRequestAllowed && !buyHours) ||
    (userSettings.isSellRequestAllowed && !sellHours)

  useEffect(() => {
    if (!isLoadingEntitlement && myEntitlement) {
      setHolidayEntitlement({
        totalHours: myEntitlement.totalHours,
        approvedHours: myEntitlement.approvedHours,
        remainingHours: myEntitlement.remainingHours,
        hostName: myEntitlement.hostName!,
        status: myEntitlement.status!,
      })
    }
  }, [myEntitlement, isLoadingEntitlement])

  const hasBuyOrSell = useMemo(() => {
    if (!userSettings) return false
    return userSettings.isBuyRequestAllowed || userSettings.isSellRequestAllowed
  }, [userSettings])

  const progBarDataSet = useMemo(() => {
    if (!isLoading) {
      const dataSet = []

      dataSet.push({
        val: holidayEntitlement.approvedHours,
        max: holidayEntitlement.totalHours,
        color: palette.type.holiday.color,
        remaining: holidayEntitlement.remainingHours,
      })

      if (userSettings.isBuyRequestAllowed && buyHours) {
        dataSet.push({
          val: buyHours.boughtHours,
          max: buyHours.availableHours,
          color: palette.type.buy.color,
          remaining: buyHours.availableHours - buyHours.boughtHours,
        })
      }

      if (userSettings.isSellRequestAllowed && sellHours) {
        dataSet.push({
          val: sellHours.soldHours,
          max: sellHours.availableHours,
          color: palette.type.sell.color,
          remaining: sellHours.availableHours - sellHours.soldHours,
        })
      }
      return dataSet
    }
    return [
      {
        val: 0,
        max: 0,
        color: '#',
        remaining: 0,
      },
    ]
  }, [isLoading, buyHours, holidayEntitlement, sellHours, userSettings])

  const filterArray = useMemo(() => {
    const arr: string[] = ['holiday']
    if (userSettings?.isBuyRequestAllowed) {
      arr.push('buy')
    }
    if (userSettings?.isSellRequestAllowed) {
      arr.push('sell')
    }
    return arr
  }, [userSettings?.isBuyRequestAllowed, userSettings?.isSellRequestAllowed])

  const popperText = useMemo(
    () => (
      <>
        {userSettings?.isBuyRequestAllowed && (
          <Paragraph>
            <span style={{ fontWeight: 'bold' }}>Buy: </span>
            <>
              You may buy up to 5 days additional holiday each holiday year as long as the total
              holiday taken does not exceed 30 days. Please refer to the bet365 Hub for further
              information regarding this policy.
            </>
          </Paragraph>
        )}
        {userSettings?.isSellRequestAllowed && (
          <Paragraph padding={userSettings?.isBuyRequestAllowed ? '20px 0' : undefined}>
            <span style={{ fontWeight: 'bold' }}>Sell: </span>
            <>
              You may sell up to 5 days holiday in each holiday year. Please refer to the bet365 Hub
              for further information regarding this policy.
            </>
          </Paragraph>
        )}
      </>
    ),
    [userSettings?.isBuyRequestAllowed, userSettings?.isSellRequestAllowed]
  )

  return (
    <>
      <Drawer
        isOpen={openDrawer}
        onClose={() => {
          setOpenDrawer(false)
        }}
        title={drawerType}
        showOptions={false}
        narrow
        mobileHeight="370px"
      >
        <Box sx={{ p: isMobPortrait ? 0 : 4 }}>
          {drawerType === 'Buy' ? (
            <BuyRequest
              closeDrawer={() => setOpenDrawer(false)}
              hours={buyHours}
              drawerType={drawerType}
            />
          ) : (
            <SellRequest
              closeDrawer={() => setOpenDrawer(false)}
              hours={sellHours}
              myEntitlement={myEntitlement}
              requestedSellHours={requestedSellHours}
              refreshEntitlement={refreshEntitlement}
              drawerType={drawerType}
            />
          )}
        </Box>
      </Drawer>
      <Card
        title={title}
        icon={hasBuyOrSell ? <InfoPopper popperText={popperText} /> : undefined}
        noDivider
      >
        <FullComponentLoadingIcon
          loading={isLoading}
          noData={!(holidayEntitlement && holidayEntitlement.totalHours)}
          loadingHeight="457px"
        >
          <Grid
            container
            spacing={4}
            flexGrow="1"
            mt={0}
            height="100%"
            alignContent="space-between"
          >
            <Grid item xs={12} height="300px">
              <CircularProgressBar
                dataSet={progBarDataSet}
                loading={isLoading}
                label={progBarDataSet?.length === 1 ? progBarDataSet[0].remaining : 0}
              />
            </Grid>
            <Grid item xs={12} sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center' }}>
              <LegendItem
                color={palette.type.holiday.color}
                type={palette.type.holiday.label}
                value={`${holidayEntitlement?.approvedHours.toFixed(
                  2
                )} / ${holidayEntitlement?.totalHours.toFixed(2)}`}
                divider
              />
              {hasBuyOrSell && (
                <>
                  {userSettings?.isBuyRequestAllowed && (
                    <LegendItem
                      color={palette.type.buy.color}
                      type="Bought"
                      value={`${buyHours?.boughtHours.toFixed(2) ?? 0}
                       / ${buyHours?.availableHours.toFixed(2) ?? 0}`}
                      divider
                      showLink
                      linkLabel="Buy"
                      url={() => {
                        actionsService.userAnalytics
                          .postEvent(
                            'UIClickEvent',
                            'MyExtrasBuyButtonClick',
                            window.location.pathname,
                            window.location.hostname
                          )
                          .catch((error: unknown) =>
                            console.error('Analytics tracking error:', error)
                          )
                        setOpenDrawer(true)
                        setDrawerType('Buy')
                      }}
                      dataTestId="MyExtras-BuyBtn"
                    />
                  )}
                  {userSettings?.isSellRequestAllowed && (
                    <LegendItem
                      color={palette.type.sell.color}
                      type="Sold"
                      value={`${sellHours?.soldHours.toFixed(2) ?? 0}
                     / ${sellHours?.availableHours.toFixed(2) ?? 0}`}
                      divider
                      showLink
                      linkLabel="Sell"
                      url={() => {
                        actionsService.userAnalytics
                          .postEvent(
                            'UIClickEvent',
                            'MyExtrasSellButtonClick',
                            window.location.pathname,
                            window.location.hostname
                          )
                          .catch((error: unknown) =>
                            console.error('Analytics tracking error:', error)
                          )
                        fetchSellData?.()
                        setOpenDrawer(true)
                        setDrawerType('Sell')
                      }}
                      dataTestId="MyExtras-SellBtn"
                    />
                  )}
                </>
              )}
              {hasBuyOrSell && (
                <Grid item xs={12} mt={6}>
                  <Link
                    linkText="Show All Requests"
                    onClick={() => {
                      actionsService.userAnalytics
                        .postEvent(
                          'UIClickEvent',
                          'MyExtrasShowAllRequestsClick',
                          window.location.pathname,
                          window.location.hostname
                        )
                        .catch((error: unknown) =>
                          console.error('Analytics tracking error:', error)
                        )
                      dispatch(setActiveRoute(`/myavailability`))
                      navigate('/myavailability/', {
                        state: { calendar: false, gridFilters: filterArray },
                      })
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </FullComponentLoadingIcon>
      </Card>
    </>
  )
}

export default AllowanceChart
